import { useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { overviewApi } from './api'
import type { OverviewCustomRange, OverviewPeriod } from './types'

export function useDashboardSummary(
  period: OverviewPeriod,
  custom?: OverviewCustomRange,
  fiscalStartMonth?: number,
) {
  const tenantId = useTenant()
  // Why: include custom from/to in the query key so switching range
  // re-fetches; LFY also reflects the fiscal start month so a tenant
  // changing their fiscal year refreshes the dashboard.
  const customSig = period === 'CUSTOM' && custom ? `${custom.from}_${custom.to}` : ''
  const lfySig = period === 'LFY' && fiscalStartMonth ? `fy${fiscalStartMonth}` : ''
  return useQuery({
    queryKey: tenantKey(tenantId, 'dashboard', 'summary', period, customSig, lfySig),
    queryFn: () => overviewApi.summary(period, custom, fiscalStartMonth),
    // Why: don't fire the request until both endpoints of the custom range
    // are filled in, otherwise the backend would 400 on every keystroke.
    enabled: period !== 'CUSTOM' || !!(custom?.from && custom?.to),
  })
}

export function usePendingDetails(enabled: boolean) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'dashboard', 'pending-details'),
    queryFn: overviewApi.pendingDetails,
    enabled,
    refetchInterval: enabled ? 2000 : false,
    refetchIntervalInBackground: false,
  })
}

export function useBudgetDashboardSummary(window?: { from: string; to: string }) {
  const tenantId = useTenant()
  const sig = window?.from && window?.to ? `${window.from}_${window.to}` : 'current-month'
  return useQuery({
    queryKey: tenantKey(tenantId, 'budget', 'dashboard-summary', sig),
    queryFn: () => overviewApi.budgetSummary(window),
  })
}

export function useInvalidateDashboard() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return () => {
    qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'dashboard') })
    qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'budget') })
  }
}
